import React from "react";

import { Button, InfoBar, type ManagedModalComponentProps } from "@shapr3d/frontend-lib";
import { upperFirst } from "es-toolkit";

import useTranslation from "../../../../../hooks/useTranslation";
import { Modal, type ModalName } from "../../../../../lib/modal";

import style from "./RemoveConfirmationModal.module.css";

interface RemoveConfirmationModalProps {
  modalProps: ManagedModalComponentProps;
  telemetryName: ModalName;
  entity: string;
  isPending?: boolean;
  isError?: boolean;
  onConfirm: () => Promise<unknown>;
  action?: "deny" | "remove" | "leave";
}

export const RemoveConfirmationModal = React.forwardRef<HTMLElement, RemoveConfirmationModalProps>(
  (
    {
      modalProps,
      telemetryName,
      entity,
      isPending = false,
      isError = false,
      onConfirm,
      action = "remove",
    }: RemoveConfirmationModalProps,
    ref
  ) => {
    const { t, m } = useTranslation();

    const modalTitle = t(m.ShareModal.RemoveConfirmationModal.Title, {
      action,
      entity,
    });

    const modalDescription = t(m.ShareModal.RemoveConfirmationModal.Description, {
      action,
      entity,
    });

    const actionLabel = upperFirst(action);

    return (
      <Modal
        {...modalProps}
        size="small"
        title={modalTitle}
        ref={ref}
        telemetryName={telemetryName}
      >
        <div className={style.deleteConfirmationContainer}>
          <span>{modalDescription}</span>
          {isError && <InfoBar variant={"critical"} title={t(m.ErrorMessage.Unknown)} />}
          <Button
            variant={"red"}
            disabled={isPending}
            onClick={async () => {
              try {
                await onConfirm?.();
              } catch {
                // This is not to throw unhandled promise rejection error
              }
            }}
          >
            {actionLabel}
          </Button>
        </div>
      </Modal>
    );
  }
);
